/**
 * =============================================================================
 * setera-web
 * =============================================================================
 * Copyright (c) 2026 HG Digital/Setera
 * Todos os direitos reservados.
 *
 * Este software é confidencial e de propriedade da HG Digital/Setera.
 * Seu uso está sujeito aos termos do contrato de licença.
 * -----
 * Criado em : 20-03-2026 10:10:38
 * Autor : diegobrock
 * Projeto : setera-web
 * Versão : 1.0.0
 * =============================================================================
 */

import { Injectable } from "@angular/core";
import Keycloak from "keycloak-js/lib/keycloak.js";
import { environment } from "src/environments/environment";
import storageX from "../storageCore/storageX";
import {
  STORAGE_KEY_ACCESS_TOKEN_KEYCLOAK,
  STORAGE_KEY_CD_USUARIO,
} from "../storageCore/constStorageKeys";
import { syncKeycloakCookie } from "../utils/keycloak-cookie.util";

@Injectable({
  providedIn: "root",
})
export class KeycloakAuthService {
  private static readonly RECOVERY_KEY = "kc_init_recovery_once";
  private initialized = false;
  private initPromise: Promise<boolean> | null = null;
  private keycloak: any;

  constructor() {
    this.keycloak = new (Keycloak as any)({
      url: environment.keycloakConfig.url,
      realm: environment.keycloakConfig.realm,
      clientId: environment.keycloakConfig.clientId,
    });
  }

  private getKeycloakInstanceSafe(): any {
    try {
      return this.keycloak;
    } catch (error) {
      return null;
    }
  }

  public async init(): Promise<boolean> {
    this.logCallbackContext("init:start");
    if (this.initialized) {
      this.persistToken();
      this.logCallbackContext("init:already-initialized");
      return this.isAuthenticated();
    }
    if (this.initPromise) {
      console.info("[KC][init] reutilizando initPromise pendente");
      return this.initPromise;
    }

    // check-sso (e não login-required): evita que o keycloak-js redirecione
    // sozinho para o login quando um refresh falhar no meio da sessão
    // (clearToken() só chama kc.login() automaticamente com login-required).
    this.initPromise = this.keycloak
      .init({
        onLoad: "check-sso",
        checkLoginIframe: false,
        pkceMethod: "S256",
      })
      .then(async (authenticated) => {
        console.info("[KC][init] concluido", {
          authenticated,
          hasCodeState: this.hasAuthCallbackParams(),
          url: window.location.href,
        });
        this.initialized = true;

        if (!authenticated) {
          // Sem sessão SSO no boot: comportamento igual ao login-required
          await this.login(window.location.href);
          return false;
        }

        this.registerTokenExpiredHandler();
        this.persistToken();
        return authenticated;
      })
      .catch((error) => {
        console.error("[KC][init] erro", {
          error,
          hasCodeState: this.hasAuthCallbackParams(),
          search: window.location.search,
          href: window.location.href,
        });
        return this.handleInitError(error);
      })
      .finally(() => {
        console.info("[KC][init] finally: limpando initPromise");
        this.initPromise = null;
      });

    return this.initPromise;
  }

  public async login(redirectUri?: string): Promise<void> {
    console.info("[KC][login] redirecionando para keycloak", {
      redirectUri: redirectUri || window.location.origin + "/",
      fromUrl: window.location.href,
    });
    await this.keycloak.login({
      redirectUri: redirectUri || window.location.origin + "/",
    });
  }

  public async logout(redirectUri?: string): Promise<void> {
    console.info("[KC][logout] iniciando logout", {
      redirectUri: redirectUri || window.location.origin + "/",
    });
    this.clearRecoveryAttempted();
    syncKeycloakCookie(undefined);
    storageX.whereKeyIs(STORAGE_KEY_ACCESS_TOKEN_KEYCLOAK).remove();
    this.initialized = false;
    this.initPromise = null;
    try {
      await this.keycloak.logout({
        redirectUri: redirectUri || window.location.origin + "/",
      });
    } catch (error) {
      console.error("[KC][logout] falha ao redirecionar logout", error);
    }
  }

  /**
   * @param minValiditySeconds use -1 para forçar o refresh incondicionalmente
   */
  public async updateToken(minValiditySeconds: number = 30): Promise<string> {
    if (!this.initialized) {
      await this.init();
    }

    if (!this.isAuthenticated()) {
      return "";
    }

    try {
      await this.keycloak.updateToken(minValiditySeconds);
      this.persistToken();
      return this.keycloak.token || "";
    } catch (error) {
      // Refresh falhou: o keycloak-js limpa o token em memória (clearToken).
      // NÃO chamamos persistToken() aqui, senão o token salvo no storage seria
      // removido e as chamadas às APIs perderiam o Authorization (tela trava).
      throw error;
    }
  }

  /**
   * Renova o token proativamente quando o access token expira, mantendo o
   * refresh token sempre recente (evita 400 invalid_grant por idle da sessão).
   */
  private registerTokenExpiredHandler(): void {
    this.keycloak.onTokenExpired = () => {
      this.updateToken(30).catch((error) => {
        console.warn(
          "[KC][onTokenExpired] refresh falhou; sessão pode ter expirado no servidor",
          error
        );
      });
    };
  }

  public getToken(): string {
    const keycloak = this.getKeycloakInstanceSafe();
    return keycloak?.token || "";
  }

  public getUsername(): string {
    const keycloak = this.getKeycloakInstanceSafe();
    const tokenParsed = keycloak?.tokenParsed;
    if (!tokenParsed) {
      return "";
    }
    return (
      (tokenParsed.preferred_username as string) ||
      (tokenParsed.email as string) ||
      ""
    );
  }

  public getLegacyUserId(): string {
    const keycloak = this.getKeycloakInstanceSafe();
    const tokenParsed = keycloak?.tokenParsed;
    if (!tokenParsed) {
      return "";
    }
    return String(tokenParsed.legacy_user_id || "");
  }

  public isAuthenticated(): boolean {
    const keycloak = this.getKeycloakInstanceSafe();
    return !!keycloak?.authenticated;
  }

  public hasAuthCallbackParams(): boolean {
    const search = window.location.search || "";
    return (
      search.indexOf("code=") > -1 &&
      search.indexOf("state=") > -1
    );
  }

  public clearAuthCallbackParams(): void {
    const cleanedUrl = window.location.origin + window.location.pathname;
    console.warn("[KC][callback] limpando parametros code/state", {
      from: window.location.href,
      to: cleanedUrl,
    });
    window.history.replaceState({}, document.title, cleanedUrl);
  }

  private async handleInitError(error: any): Promise<boolean> {
    const hasCallback = this.hasAuthCallbackParams();
    const recoveryAttempted = this.hasRecoveryAttempted();
    console.warn("[KC][init] acionando estrategia de recuperacao", {
      hasCallback,
      recoveryAttempted,
      href: window.location.href,
    });

    if (hasCallback && !recoveryAttempted) {
      this.markRecoveryAttempted();
      this.clearAuthCallbackParams();
      await this.login(window.location.origin + window.location.pathname);
      return false;
    }

    this.clearRecoveryAttempted();
    throw error;
  }

  private hasRecoveryAttempted(): boolean {
    try {
      return (
        window.sessionStorage.getItem(KeycloakAuthService.RECOVERY_KEY) === "1"
      );
    } catch (error) {
      return false;
    }
  }

  private markRecoveryAttempted(): void {
    try {
      window.sessionStorage.setItem(KeycloakAuthService.RECOVERY_KEY, "1");
    } catch (error) {
      // noop
    }
  }

  private clearRecoveryAttempted(): void {
    try {
      window.sessionStorage.removeItem(KeycloakAuthService.RECOVERY_KEY);
    } catch (error) {
      // noop
    }
  }

  private persistToken(): void {
    const keycloak = this.getKeycloakInstanceSafe();
    const token = keycloak?.token;
    if (!token) {
      syncKeycloakCookie(undefined);
      storageX.whereKeyIs(STORAGE_KEY_ACCESS_TOKEN_KEYCLOAK).remove();
      return;
    }
    syncKeycloakCookie(token);
    storageX.whereKeyIs(STORAGE_KEY_ACCESS_TOKEN_KEYCLOAK).set(token);
    const legacyUserId = this.getLegacyUserId();
    if (legacyUserId) {
      storageX.whereKeyIs(STORAGE_KEY_CD_USUARIO).set(legacyUserId);
    }
    console.info("[KC][persistToken] token persistido", {
      hasToken: !!token,
      hasLegacyUserId: !!legacyUserId,
      authenticated: this.isAuthenticated(),
      hasCodeState: this.hasAuthCallbackParams(),
    });
    this.clearRecoveryAttempted();
  }

  private logCallbackContext(stage: string): void {
    console.info(`[KC][${stage}] contexto`, {
      href: window.location.href,
      search: window.location.search,
      hasCodeState: this.hasAuthCallbackParams(),
      initialized: this.initialized,
      hasInitPromise: !!this.initPromise,
      isAuthenticated: this.isAuthenticated(),
    });
  }
}
